/**
 * API helper для работы с NeuroFox API
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Типы для API
export interface UserProfile {
  id: number;
  telegram_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  current_chapter: string;
  level: number;
  xp: number;
  is_pro: boolean;
  created_at: string;
  last_activity?: string;
}

export interface LogResponse {
  success: boolean;
  message: string;
  xp_earned?: number;
  data?: any;
}

export interface InsightsResponse {
  today: {
    sleep_hours?: number;
    steps?: number;
    screen_time_minutes?: number;
    mood_score?: number;
    is_complete: boolean;
  };
  week: {
    total_steps: number;
    avg_sleep: number;
    avg_mood: number;
    days_logged: number;
  };
  month: {
    total_steps: number;
    avg_sleep: number;
    days_logged: number;
  };
  tips: string[];
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: UserProfile;
}

// JWT storage
const JWT_KEY = 'neurofox_jwt';

export const getJWT = (): string | null => {
  return localStorage.getItem(JWT_KEY);
};

export const setJWT = (token: string): void => {
  localStorage.setItem(JWT_KEY, token);
};

export const removeJWT = (): void => {
  localStorage.removeItem(JWT_KEY);
};

// API fetch helper
export const apiFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<any> => {
  const token = getJWT();
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (response.status === 401) {
      // Token expired or invalid
      removeJWT();
      throw new Error('Unauthorized - please re-authenticate');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Auth API
export const authTelegram = async (initData: string): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/telegram`, {
    method: 'GET',
    headers: {
      'X-Telegram-Init-Data': initData,
    },
  });
  
  if (!response.ok) {
    throw new Error('Authentication failed');
  }
  
  const data = await response.json();
  setJWT(data.access_token);
  return data;
};

// User API
export const getProfile = async (): Promise<UserProfile> => {
  return apiFetch('/me');
};

// Logging API
export const logSleep = async (data: {
  hours: number;
  minutes?: number;
  quality?: number;
}): Promise<LogResponse> => {
  return apiFetch('/log/sleep', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const logSteps = async (data: {
  steps: number;
  calories_burned?: number;
}): Promise<LogResponse> => {
  return apiFetch('/log/steps', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const logScreenTime = async (data: {
  minutes: number;
}): Promise<LogResponse> => {
  return apiFetch('/log/screen', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const logMood = async (data: {
  score: number;
  note?: string;
}): Promise<LogResponse> => {
  return apiFetch('/log/mood', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const logDiary = async (data: {
  content: string;
}): Promise<LogResponse> => {
  return apiFetch('/log/diary', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Insights API
export const getInsights = async (): Promise<InsightsResponse> => {
  return apiFetch('/insights/today');
};

// Health check
export const checkHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/healthz`);
    return response.ok;
  } catch {
    return false;
  }
};

// AI функции
export interface AIAnalysisResponse {
  analysis: string;
  insights: string[];
  recommendations: string[];
}

export interface AIAdviceRequest {
  context: string;
}

export interface AIAdviceResponse {
  advice: string;
}

export interface AIHabitsPlanResponse {
  plan: string;
  habits: string[];
  duration: string;
  difficulty: string;
}

export interface AIHealthQuestionRequest {
  question: string;
}

export interface AIHealthQuestionResponse {
  answer: string;
}

export async function getAIAnalysis(): Promise<AIAnalysisResponse> {
  return apiFetch('/ai/analyze', { method: 'POST' });
}

export async function getAIAdvice(context: string): Promise<AIAdviceResponse> {
  return apiFetch('/ai/advice', {
    method: 'POST',
    body: JSON.stringify({ context })
  });
}

export async function getAIHabitsPlan(): Promise<AIHabitsPlanResponse> {
  return apiFetch('/ai/habits-plan', { method: 'POST' });
}

export async function getAIHealthAnswer(question: string): Promise<AIHealthQuestionResponse> {
  return apiFetch('/ai/health-question', {
    method: 'POST',
    body: JSON.stringify({ question })
  });
}
